import React from 'react';
import {
  AreaChart, Area, Line,
  YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Machine } from '../types';

interface Props { machine: Machine; }

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativa',
  idle: 'Ociosa',
  maintenance: 'Manutenção',
  offline: 'Offline',
};

const STATUS_COLOR: Record<string, string> = {
  active: '#10b981',
  idle: '#f59e0b',
  maintenance: '#8b5cf6',
  offline: '#ef4444',
};

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1e2d45', border: '1px solid #1c2a40',
      borderRadius: 8, padding: '8px 12px', fontSize: 11,
    }}>
      <div style={{ color: '#94a3b8', marginBottom: 4 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.color, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
          {p.name}: {p.value?.toLocaleString('pt-BR')}
        </div>
      ))}
    </div>
  );
}

export default function MachineCard({ machine }: Props) {
  const { production, status, history } = machine;
  const pct = Math.min(production.efficiency, 100);
  const fillClass = pct >= 90 ? 'success' : pct < 40 ? 'warning' : '';
  const color = STATUS_COLOR[status];

  return (
    <div className={`machine-card status-${status}`}>
      {/* Header */}
      <div className="card-header">
        <div className="card-header-left">
          <div className="machine-number">{machine.number.toString().padStart(2, '0')}</div>
          <div className="card-name-block">
            <div className="machine-name">{machine.name}</div>
            <div className="machine-model">{machine.model} · {machine.type}</div>
          </div>
        </div>
        <div className={`status-badge ${status}`}>
          <span className="status-dot" />
          {STATUS_LABELS[status]}
        </div>
      </div>

      {/* 3 métricas */}
      <div className="metrics-grid">
        <div className="metric-box">
          <div className="metric-val blue">{production.current.toLocaleString('pt-BR')}</div>
          <div className="metric-lbl">Produzido</div>
        </div>
        <div className="metric-box">
          <div className="metric-val">{production.target.toLocaleString('pt-BR')}</div>
          <div className="metric-lbl">Meta</div>
        </div>
        <div className="metric-box">
          <div className={`metric-val ${status === 'active' ? 'green' : ''}`}>
            {status === 'active' ? production.hourly : '—'}
          </div>
          <div className="metric-lbl">Pç / hora</div>
        </div>
      </div>

      {/* Progresso */}
      <div className="progress-wrap">
        <div className="progress-head">
          <span className="progress-title">Meta do turno</span>
          <span className="progress-pct">{pct}%</span>
        </div>
        <div className="progress-track">
          <div className={`progress-fill ${fillClass}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Operadora */}
      <div className="operator-row">
        {machine.operator ? (
          <div className="operator-info">
            <div className="operator-avatar" style={{ background: machine.operator.avatarColor }}>
              {machine.operator.initials}
            </div>
            <div className="operator-details">
              <div className="operator-name">{machine.operator.name}</div>
              <div className="operator-since">Turno {machine.operator.shift}</div>
            </div>
          </div>
        ) : (
          <div className="operator-info">
            <div className="operator-avatar" style={{ background: '#374151' }}>—</div>
            <div className="operator-details">
              <div className="operator-name" style={{ color: 'var(--text-muted)' }}>
                {status === 'maintenance' ? 'Em manutenção' : 'Sem operador'}
              </div>
            </div>
          </div>
        )}
        <span className="uptime-val" style={{ color: machine.uptime > 80 ? 'var(--status-active)' : 'var(--text-muted)' }}>
          {machine.uptime > 0 ? `${machine.uptime}% uptime` : ''}
        </span>
      </div>

      {/* Gráfico */}
      <div className="chart-section">
        <ResponsiveContainer width="100%" height={80}>
          <AreaChart data={history.slice(-12)} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${machine.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
                <stop offset="95%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1c2a40" vertical={false} />
            <YAxis tick={{ fill: '#4a5568', fontSize: 9 }} axisLine={false} tickLine={false}
              tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : String(v)} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="produced" name="Produzido"
              stroke={color} strokeWidth={1.5} fill={`url(#g-${machine.id})`}
              dot={false} activeDot={{ r: 3, fill: color }} />
            <Line type="monotone" dataKey="target" name="Meta"
              stroke="#374151" strokeWidth={1} strokeDasharray="4 3" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
